import { useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { Text } from '~components/Typography';
import { useRetainedValue } from '~hooks/useRetainedValue';
import { useTranslations } from '~i18n/hooks';
import { usePathname, useRouter } from '~i18n/navigation';
import CloseIcon from '~public/assets/icons/close.svg';
import SearchIcon from '~public/assets/icons/search.svg';
import { ButtonGhost } from '~shared/ui/atoms/Buttons/ButtonGhost';
import { ButtonStroked } from '~shared/ui/atoms/Buttons/ButtonStroked';
import { InputWithIcons } from '~shared/ui/molecules/InputWithIcons';
import { updateQueryParamsWithPathname } from '~utils/searchParams';

import { CUSTOM_VALUE_QUERY_PARAM_VALUE } from '../../model/constants';
import { isSingleValueMultiSelect } from '../../model/guards';
import type {
	BotWizardFieldButton,
	BotWizardFieldDataButtonItem,
	BotWizardFieldNames,
	BotWizardValues,
} from '../../model/types';
import { getDependencyContent } from '../../model/utils';
import { WorkingArea } from '../styles';
import { Buttons, input, StyledFieldButton } from './styles';

export const FieldButton = ({
	id,
	values,
	openModal,
	customValues,
	...field
}: BotWizardFieldButton & {
	id: BotWizardFieldNames;
	values: BotWizardValues;
	openModal: () => void;
	customValues: { [key in BotWizardFieldNames]?: string };
}) => {
	const t = useTranslations('BotWizard');
	const {
		data,
		contentDependency,
		queryKey,
		allowCustom,
		withSearch,
		multiSelect,
	} = field;
	const [search, setSearch] = useState('');
	const [visibleCount, setVisibleCount] = useState(15);
	const pathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();
	const localData = getDependencyContent({
		data,
		contentDependency,
		values,
	});

	const getHref = (item: BotWizardFieldDataButtonItem) => {
		if (multiSelect) {
			let newValue = [...getMultiSelectValue()];
			const selected = newValue.includes(item.id);
			if (selected) {
				newValue = newValue.filter((id) => id !== item.id);
			} else {
				newValue.push(item.id);
			}
			return newValue;
		} else {
			return item.id;
		}
	};

	const [value, setValue] = useRetainedValue(
		values[id] as string | string[] | undefined,
		(v) => {
			router.push(
				updateQueryParamsWithPathname({ [queryKey]: v }, pathname, searchParams)
			);
		}
	);

	if (!localData) {
		return null;
	}

	const getMultiSelectValue = () => {
		const v = values[id];
		if (isSingleValueMultiSelect(field, v)) {
			return v ?? [];
		} else return [];
	};

	const selectedData = localData.filter((el) =>
		getMultiSelectValue().includes(el.id)
	);

	const selectedItems = multiSelect
		? getMultiSelectValue().length
		: value
		? 1
		: 0;

	return (
		<WorkingArea>
			<StyledFieldButton>
				{withSearch && (
					<InputWithIcons
						className={input}
						leftIcon={<SearchIcon />}
						rightIcon={search.length ? <CloseIcon /> : undefined}
						onRightIconClick={() => setSearch('')}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						uiSize="L"
						placeholder={t('searchPlaceholderButtonField')}
					/>
				)}
				{multiSelect && (
					<Buttons>
						{selectedData.map((el) => (
							<ButtonStroked
								onClick={() => {
									setValue(getHref(el));
								}}
								borderRadius="XS"
								selected={true}
								size="M"
								key={el.id}
								rightIcon={<CloseIcon />}
							>
								{el.text}
							</ButtonStroked>
						))}
					</Buttons>
				)}
				{selectedItems >= 30 && (
					<Text size={'L'} color="warn">
						{t('maxMultiselectItems')}
					</Text>
				)}
				<Buttons>
					{localData
						.filter(
							(el) =>
								(multiSelect && !getMultiSelectValue().includes(el.id)) ||
								!multiSelect
						)
						.filter((el) =>
							el.text.toLowerCase().includes(search.toLowerCase())
						)
						.filter((_, index) => search || index < visibleCount)
						.map((el) => (
							<ButtonStroked
								onClick={() => {
									setValue(getHref(el));
								}}
								disabled={selectedItems >= 30}
								borderRadius="XS"
								selected={value === el.id}
								size="L"
								key={el.id}
							>
								{el.text}
							</ButtonStroked>
						))}
					{customValues[id] && (
						<ButtonStroked
							borderRadius="XS"
							size="L"
							onClick={() => {
								setValue(CUSTOM_VALUE_QUERY_PARAM_VALUE);
							}}
							selected={value === CUSTOM_VALUE_QUERY_PARAM_VALUE}
						>
							{customValues[id]}
						</ButtonStroked>
					)}
					{allowCustom && (
						<ButtonStroked borderRadius="XS" size="L" onClick={openModal}>
							{t('customOptionTitle')}
						</ButtonStroked>
					)}
				</Buttons>
				{localData.length > visibleCount + selectedData.length && !search && (
					<ButtonGhost
						onClick={() => setVisibleCount((old) => old + 10)}
						size="L"
					>
						{t('showMoreButtonField', {
							count:
								(localData.length || 0) - visibleCount - selectedData.length,
						})}
					</ButtonGhost>
				)}
			</StyledFieldButton>
		</WorkingArea>
	);
};
